<template>
  <base-page-layout :title="alteracaoTitle" class="alteracao-encartes">
    <mf-loading loader-type="dialog" :boolean-loader="loading">
      <title-description
        id="flyer-title"
        class="mb-4"
        :title.sync="flyer.title"
        :description.sync="flyer.description"
        :title-error="$v.flyer.title.$error"
        :title-error-message="errorMessages.name"
        :description-error="$v.flyer.description.$error"
        :description-error-message="errorMessages.description"
        :disable-title="readOnly"
        :disable-description="readOnly"
      />
      <v-row>
        <v-col cols="3" class="d-flex">
          <mf-card-container title="Lojas de exibição" class="flex-grow-1">
            <v-row>
              <v-col>
                <div class="card-inputs">
                  <mf-select-units-dialog
                    label="Lojas"
                    :selected-units.sync="flyer.units"
                    :unit-error="$v.flyer.units.$error"
                    :units="units"
                    :tag-list="tagList"
                    :group-list="unitsGroups"
                    :disabled="readOnly"
                    hide-ecommerce-configuration
                  />
                </div>
              </v-col>
            </v-row>
          </mf-card-container>
        </v-col>
        <v-col cols="9" class="d-flex">
          <mf-card-container title="Período de exibição" class="flex-grow-1">
            <v-row>
              <v-col cols="6" class="pr-6">
                <v-row>
                  <v-col cols="6" class="py-0">
                    <date-picker
                      class="date-picker"
                      :min="DateTime.now().toFormat('yyyy-MM-dd')"
                      :date.sync="initialDate"
                      :error-messages="initialDateTimeErrorMessages"
                      :disabled="readOnly"
                      outlined
                      label="Data inicial"
                    />
                  </v-col>
                  <v-col cols="6" class="py-0">
                    <mf-time-picker
                      v-model="initialTime"
                      outline
                      label="Hora inicial"
                      :disabled="readOnly"
                      outlined
                      :error-messages="initialDateTimeErrorMessages"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="6" class="pl-6">
                <v-row>
                  <v-col cols="6" class="py-0">
                    <date-picker
                      class="date-picker"
                      :min="DateTime.now().toFormat('yyyy-MM-dd')"
                      :date.sync="finalDate"
                      :error-messages="finalDateTimeErrorMessages"
                      :disabled="readOnly"
                      outlined
                      label="Data final"
                    />
                  </v-col>
                  <v-col cols="6" class="py-0">
                    <mf-time-picker v-model="finalTime" outline label="Hora final" :disabled="readOnly" outlined :error-messages="finalDateTimeErrorMessages" />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </mf-card-container>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6">
          <mf-card-container title="Visualização do encarte" class="view-booklet-card">
            <div v-if="flyer.images && flyer.images.length">
              <v-carousel class="carousel" height="490" :continuous="false" :hide-delimiters="!!(flyer.images && flyer.images.length)">
                <v-carousel-item v-for="(image, i) in flyer.images" :key="i">
                  <v-sheet height="100%">
                    <v-row class="fill-height" align="center" justify="center">
                      <img eager height="100%" position="center center" :src="image" />
                    </v-row>
                  </v-sheet>
                </v-carousel-item>
              </v-carousel>
            </div>
            <div v-else class="empty-data">
              <div class="empty-data-content text-center">
                <mf-icon size="32">content_paste_off</mf-icon>
                <span class="card-title"> Nenhum encarte selecionado </span>
              </div>
            </div>
            <input ref="file-input" class="hidden-input" type="file" @input="readFile($event)" />
            <canvas id="the-canvas" style="display: none" />
          </mf-card-container>
        </v-col>

        <v-col cols="6">
          <div class="right-cards">
            <mf-card-container title="Arquivo do encarte" class="input-image-card">
              <mf-button :loading="uploadLoading" size="sm" :disabled="readOnly" label="Selecionar arquivo" @click="activateInput" />
              <p v-if="$v.flyer.images.$error" class="error-image">É obrigatório adicionar um arquivo</p>
              <p class="input-image-card-description">
                Os formatos aceitos são: <span class="green-marking">PNG, JPG, PDF ou JPEG.</span> Recomendamos a utilização de arquivos
                <span class="green-marking">PDF</span> com várias páginas, com dimensões aproximadas a
                <span class="green-marking">220×310(retrato) pixels</span> para melhor visualização em inúmeros modelos de dispositivos mobile. O tamanho máximo
                permitido é de <span class="green-marking">até 100 MB.</span>
              </p>
            </mf-card-container>

            <mf-card-container title="Prioridade" class="priority-card">
              <p class="card-subtitle">
                A prioridade define qual encarte deve ser exibido primeiro. Por exemplo, encartes de prioridade alta serão exibidos na primeira posição.
              </p>
              <v-radio-group v-model="flyer.priority" :disabled="readOnly" row dense hide-details class="mt-0">
                <v-radio label="Alta" color="primary" :value="isFranchise || readOnly ? 3 : 6" />
                <v-radio label="Média" color="primary" :value="isFranchise || readOnly ? 2 : 5" />
                <v-radio label="Baixa" color="primary" :value="isFranchise || readOnly ? 1 : 4" />
              </v-radio-group>
              <p v-if="$v.flyer.priority.$error" class="error-image">É obrigatório selecionar uma prioridade</p>
            </mf-card-container>
          </div>
        </v-col>
      </v-row>

      <action-buttons
        :is-create="isNewFlyer"
        :status="flyer.status.toLowerCase()"
        :loading="uploadLoading"
        :readOnly="readOnly"
        @delete="removeFlyer()"
        @saveDraft="saveFlyer(false)"
        @disable="disableFlyer"
        @enable="saveFlyer(true)"
        @cancel="backToFlyersList()"
      />
    </mf-loading>
  </base-page-layout>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import BasePageLayout from '@/components/BasePageLayout.vue'
import TitleDescription from '@/modules/campanhas/views/components/edit/TitleDescription.vue'
import ActionButtons from '@/modules/campanhas/views/components/ActionButtons.vue'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { isGreaterOrEqualThanFieldString, isLesserOrEqualThanFieldString } from '@/helpers/validators.js'
import {
  QUERY_FLYER,
  MUTATION_UPSERT_DRAFT_FLYER,
  MUTATION_UPSERT_FLYER,
  MUTATION_DELETE_FLYER,
  MUTATION_TOGGLE_FLYER,
  QUERY_ACTIVE_UPLOAD_AND_CONVERT_PAGES_TO_IMAGES_JOB,
  UPLOAD_AND_CONVERT_PAGES_TO_IMAGES,
} from '../../graphql'
import Swal from 'sweetalert2'
import { deepDelete } from '@/helpers/deepDelete'
import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import { wsClient } from '@/services/webSocket'

export default {
  components: {
    BasePageLayout,
    TitleDescription,
    ActionButtons,
    DatePicker: () => import('@/components/DatePicker.vue'),
  },
  props: {
    newCampaign: Boolean,
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    flyer: {
      title: '',
      description: '',
      initial_date: '',
      final_date: '',
      units: [],
      medium: ['APP'],
      images: [],
      thumbnails: [],
      original_file: {
        filename: '',
        url: '',
      },
      status: '',
    },
    DateTime: DateTime,
    localInitialTime: '00:00',
    localInitialDate: '',
    localFinalTime: '23:59',
    localFinalDate: '',
    loading: false,
    uploadLoading: false,
    imagesToShow: [],
    allInputsRequired: false,
    allowedFileTypes: ['pdf', 'png', 'jpg', 'jpeg'],
    errorMessages: {
      name: 'Dê um nome para este encarte',
      description: 'De uma descrição para este encarte',
    },
    roomId: '',
  }),
  computed: {
    ...mapGetters(['units', 'tagList', 'unitsGroups']),
    initialDate: {
      get() {
        return this.localInitialDate
      },
      set(value) {
        this.localInitialDate = value
        this.flyer.initial_date = `${this.localInitialDate}T${this.localInitialTime}:00-03:00`
      },
    },
    initialTime: {
      get() {
        return this.localInitialTime
      },
      set(value) {
        this.localInitialTime = value
        this.flyer.initial_date = `${this.localInitialDate}T${this.localInitialTime}:00-03:00`
      },
    },
    finalDate: {
      get() {
        return this.localFinalDate
      },
      set(value) {
        this.localFinalDate = value
        this.flyer.final_date = `${this.localFinalDate}T${this.localFinalTime}:59-03:00`
      },
    },
    finalTime: {
      get() {
        return this.localFinalTime
      },
      set(value) {
        this.localFinalTime = value
        this.flyer.final_date = `${this.localFinalDate}T${this.localFinalTime}:00`
      },
    },
    initialDateTimeErrorMessages() {
      if (!this.$v.flyer.initial_date.$error) return ''
      if (!this.$v.flyer.initial_date.required) return 'Selecione a data inicial para este encarte'
      if (!this.$v.flyer.initial_date.lesserOrEqualThanFinalDate) return 'A data inicial precisa ser maior ou igual a data final'
      return ''
    },
    finalDateTimeErrorMessages() {
      if (!this.$v.flyer.final_date.$error) return ''
      if (!this.$v.flyer.final_date.required) return 'Selecione a data final para este encarte'
      if (!this.$v.flyer.final_date.greaterOrEqualThanInitialDate) return 'A data final precisa ser maior ou igual a data inicial'
      return ''
    },
    alteracaoTitle() {
      if (this.newCampaign) return 'Novo encarte'
      else return 'Editar encarte'
    },
    isNewFlyer() {
      return Boolean(!this.$route.params.id)
    },
    getFormatedStartDate() {
      if (!this.flyer.initial_date) return ''
      return DateTime.fromISO(this.flyer.initial_date.toString()).toUTC().toISO()
    },

    getFormatedEndDate() {
      if (!this.flyer.final_date) return ''
      return DateTime.fromISO(this.flyer.final_date.toString()).toUTC().toISO()
    },
    getFormatedUnits() {
      return this.flyer.units.map((unit) => String(unit))
    },
    isFranchise() {
      return this.$getLoggedUser().is_franchise
    },
  },
  async created() {
    if (!this.isNewFlyer) {
      await this.getData()
      if (this.flyer.initial_date) {
        this.localInitialDate = DateTime.fromISO(this.flyer.initial_date.toString()).toFormat('yyyy-MM-dd')
        this.localInitialTime = DateTime.fromISO(this.flyer.initial_date.toString()).toFormat('HH:mm')
      }
      if (this.flyer.final_date) {
        this.localFinalDate = DateTime.fromISO(this.flyer.final_date.toString()).toFormat('yyyy-MM-dd')
        this.localFinalTime = DateTime.fromISO(this.flyer.final_date.toString()).toFormat('HH:mm')
      }
    }
  },
  async mounted() {
    const roomId = uuidv4()
    this.roomId = roomId

    const self = this
    await wsClient.subscribe(
      {
        query: UPLOAD_AND_CONVERT_PAGES_TO_IMAGES,
        operationName: 'UploadAndConvertPagesToImages',
        variables: {
          roomId: this.roomId,
        },
      },
      {
        next({ data: { uploadAndConvertPagesToImages } }) {
          self.flyer.original_file.url = uploadAndConvertPagesToImages.original.url
          self.flyer.original_file.filename = uploadAndConvertPagesToImages.original.filename
          self.flyer.images = uploadAndConvertPagesToImages.images.map((image) => image.url)
          self.flyer.thumbnails = uploadAndConvertPagesToImages.thumbnails.map((thumbnail) => thumbnail.url)

          self.uploadLoading = false
        },
        error(error) {
          console.error(error)
          self.$snackbar({ message: 'Erro ao carregar produtos, tente novamente mais tarde.', snackbarColor: 'error' })
          self.uploadLoading = false
        },
      }
    )
  },
  methods: {
    async getData() {
      try {
        this.loading = true
        const { data } = await this.$apollo.query({
          query: QUERY_FLYER,
          variables: {
            _id: this.$route.params.id,
            franchise: this.readOnly,
          },
          fetchPolicy: 'no-cache',
        })
        this.flyer = data.flyer
        this.flyer.units = this.flyer.units.map((flyer) => flyer.id_erp)
      } catch (error) {
      } finally {
        this.loading = false
      }
    },

    activateInput() {
      this.$refs['file-input'].value = null
      this.$refs['file-input'].click()
    },

    readFile(event) {
      if (event.target.files.length === 0) return
      this.file = event.target.files[0]

      if (this.file.size > 100000000) {
        Swal.fire({
          text: 'Arquivo com mais de 100mb.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1800,
          customClass: {
            content: 'content-class',
          },
        })
        this.deleteFile()
      } else if (!this.validFormatType(this.file)) {
        Swal.fire({
          text: 'Formato de arquivo inválido.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1000,
          customClass: {
            content: 'content-class',
          },
        })
        this.deleteFile()
      } else {
        this.fileName = this.file.name
        this.fileExtension = this.file.name.split('.').pop()

        this.uploadAndConvertPagesToImages(this.file)
      }
    },

    validFormatType(file) {
      return this.allowedFileTypes.includes(file.name.split('.').pop().toLowerCase())
    },

    deleteFile() {
      this.file = null
      this.fileName = ''
      this.fileExtension = ''
    },

    async uploadAndConvertPagesToImages(file) {
      try {
        this.uploadLoading = true
        await this.$apollo.mutate({
          mutation: QUERY_ACTIVE_UPLOAD_AND_CONVERT_PAGES_TO_IMAGES_JOB,
          variables: {
            file,
            prefix: 'flyers',
            roomId: this.roomId,
          },
          context: {
            uri: this.$microservicesUrls['asyncJobsUpload'],
            headers: {
              'x-apollo-operation-name': 'activateUploadAndConvertPagesToImagesJob',
            },
          },
        })
      } catch (error) {
        this.$snackbar({ message: 'Falha em subir o arquivo de encartes. Por favor tenta novamente mais tarde.', snackbarColor: 'error' })
        this.uploadLoading = false
      }
    },

    async saveFlyer(enable) {
      try {
        this.allInputsRequired = enable
        const flyerToSave = cloneDeep(this.flyer)

        flyerToSave.initial_date = this.getFormatedStartDate
        flyerToSave.final_date = this.getFormatedEndDate
        flyerToSave.units = this.getFormatedUnits

        this.removeUselessProperties(flyerToSave)

        this.$v.$touch()
        if (this.$v.$error) {
          this.$snackbar({ message: 'Preencha todos os campos corretamente.', snackbarColor: 'error' })
          return
        }

        deepDelete(flyerToSave, '__typename')
        await this.$apollo.mutate({
          mutation: enable ? MUTATION_UPSERT_FLYER : MUTATION_UPSERT_DRAFT_FLYER,
          variables: {
            data: flyerToSave,
          },
        })
        this.$router.push('/mobile/encartes').catch(() => {})
      } catch (error) {
        console.error(error)
      }
    },

    async disableFlyer() {
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_TOGGLE_FLYER,
          variables: {
            _id: this.$route.params.id,
            active: false,
          },
        })
        this.$router.push('/mobile/encartes').catch(() => {})
      } catch (error) {}
    },

    removeUselessProperties(flyerToSave) {
      delete flyerToSave.status
      delete flyerToSave.scheduled
      delete flyerToSave.active
      delete flyerToSave.created_at
      delete flyerToSave.updated_at
      delete flyerToSave.__typename
      delete flyerToSave.metrics
    },

    backToFlyersList() {
      this.$router.push('/mobile/encartes').catch(() => {})
    },

    removeFlyer() {
      try {
        this.$confirm({
          title: 'Remover encarte',
          message: 'Tem certeza que deseja remover este encarte?',
          confirmText: 'Remover',
          confirmColor: 'vermelho-d20',
          cancelText: 'Cancelar',
        })
          .then(async () => {
            await this.$apollo.mutate({
              mutation: MUTATION_DELETE_FLYER,
              variables: {
                _id: this.$route.params.id,
              },
            })
            this.$router.push('/mobile/encartes').catch(() => {})
          })
          .catch(() => {})
      } catch (error) {
        this.$snackbar({ message: 'Erro ao remover encarte.', snackbarColor: 'error' })
      }
    },
  },
  validations: {
    flyer: {
      title: { required },
      description: {
        required: requiredIf(function () {
          return this.allInputsRequired
        }),
      },
      units: {
        required: requiredIf(function () {
          return this.allInputsRequired
        }),
      },
      initial_date: {
        required: requiredIf(function () {
          return this.allInputsRequired
        }),
        lesserOrEqualThanFinalDate: isLesserOrEqualThanFieldString('final_date'),
      },
      final_date: {
        required: requiredIf(function () {
          return this.allInputsRequired
        }),
        greaterOrEqualThanInitialDate: isGreaterOrEqualThanFieldString('initial_date'),
      },
      images: {
        required: requiredIf(function () {
          return this.allInputsRequired
        }),
      },
      medium: {
        required: requiredIf(function () {
          return this.allInputsRequired
        }),
      },
      priority: {
        required: requiredIf(function () {
          return this.allInputsRequired
        }),
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.alteracao-encartes {
  .card-subtitle {
    color: $cinza-0;
    font-size: 14px;
    line-height: 20px;
  }
  &-cards {
    width: 100%;
    display: flex;
    column-gap: 25px;
    margin-bottom: 24px;
    .card {
      width: 100%;
      &-inputs {
        width: 100%;
        display: flex;
        align-items: center;
        column-gap: 30px;
        ::v-deep {
          .mf-select-units-dialog-input-container {
            width: 100%;
          }
        }
      }
      .validity-card-title {
        display: flex;
        align-items: center;
        column-gap: 12px;
        @extend .card-title;
      }
    }
  }
  .view-booklet-card {
    padding: 20px;
    .carousel {
      border-radius: 8px;
    }
    .empty-data {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 490px;
      &-content {
        display: flex;
        flex-direction: column;
        row-gap: 20px;
        .card-title {
          color: $cinza-0;
        }
      }
    }
  }
  .right-cards {
    display: flex;
    flex-direction: column;
    row-gap: 22px;
    ::v-deep.v-card__text {
      padding: 32px 26px;
    }
    .input-image-card {
      &-description {
        padding-top: 20px;
        font-weight: 400;
        font-size: 14px;
        color: $cinza-0;
        .green-marking {
          font-weight: 700;
          color: $marca-1;
        }
      }
    }
  }
  .hidden-input {
    display: none;
  }
  .card-title {
    padding-left: 0;
    margin-bottom: 20px;
  }
  .info-upload {
    margin: 70px 0 0 0;
    padding: 0;

    .content-icon {
      .xl-icon {
        font-size: 104px;
      }
    }

    #upload-image-instructions {
      p {
        font-size: 18px;
        color: $marca-0-l40;
      }
    }

    .upload-content {
      padding: 0 60px;
    }
  }

  #exibition-title {
    display: inline-block;
  }

  .text-gray {
    color: $cinza-d10;
  }

  .error-image {
    color: #ff5252 !important;
    font-size: 12px;
    margin: 5px 0 0 0;
  }
}
</style>
